import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Wallet, Users, Copy, Send, ArrowRight } from "lucide-react";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

export default function WalletPage() {
  const [tab, setTab] = useState("credits");
  useDocumentTitle("Wallet");
  return (
    <DashboardShell>
      <div className="max-w-4xl" data-testid="wallet-page">
        <div className="mb-6">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Wallet</div>
          <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Credits, gift cards & referrals</h1>
        </div>

        <div className="inline-flex rounded-full border border-slate-200 bg-white p-1 mb-4" data-testid="wallet-tabs">
          {[
            { k: "credits", label: "Credits", icon: Wallet },
            { k: "giftcards", label: "Gift cards", icon: Gift },
            { k: "referrals", label: "Referrals", icon: Users },
          ].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm ${tab === t.k ? "bg-[#165a71] text-white" : "text-[#252A34]/80"}`} data-testid={`wallet-tab-${t.k}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "credits" && <CreditsTab />}
        {tab === "giftcards" && <GiftCardsTab />}
        {tab === "referrals" && <ReferralsTab />}
      </div>
    </DashboardShell>
  );
}

function CreditsTab() {
  const [d, setD] = useState({ balance: 0, entries: [] });
  useEffect(() => { api.get("/credits/mine").then((r) => setD(r.data)); }, []);
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="credit-balance">
        <div className="text-xs uppercase tracking-wider text-[#252A34]/60">Balance</div>
        <div className="mt-1 font-display text-3xl font-semibold text-[#071b22]">${(d.balance || 0).toFixed(2)}</div>
        <div className="text-xs text-[#252A34]/60 mt-2">Applied automatically at checkout before card charge.</div>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 font-display font-semibold text-[#071b22]">Ledger</div>
        {d.entries.length === 0 ? (
          <div className="p-6 text-sm text-[#252A34]/60 text-center">No credit activity yet.</div>
        ) : (
          <ul className="divide-y" data-testid="credit-entries">
            {d.entries.map((e) => (
              <li key={e.id} className="px-4 py-3 flex items-center gap-3 text-sm">
                <div className="flex-1">
                  <div className="text-[#071b22]">{e.reason || e.kind}</div>
                  <div className="text-xs text-[#252A34]/60">{new Date(e.created_at).toLocaleString()} · {e.kind}</div>
                </div>
                <div className={`font-medium ${e.amount >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{e.amount >= 0 ? "+" : ""}${Math.abs(e.amount).toFixed(2)}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function GiftCardsTab() {
  const [cards, setCards] = useState([]);
  const [amount, setAmount] = useState(50);
  const [redeemCode, setRedeemCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/giftcards/mine").then((r) => setCards(r.data || []));
  useEffect(() => { load(); }, []);

  const buy = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/giftcards", { amount });
      if (data.checkout_url) window.location.href = data.checkout_url;
      else toast.success("Gift card created");
    } catch (err) { toast.error(err?.response?.data?.error || "Failed"); } finally { setBusy(false); }
  };
  const redeem = async () => {
    try {
      const { data } = await api.post("/giftcards/redeem", { code: redeemCode });
      toast.success(`Credited $${data.credited.toFixed(2)}. New balance: $${data.balance.toFixed(2)}`);
      setRedeemCode("");
      load();
    } catch (err) { toast.error(err?.response?.data?.error || "Failed"); }
  };
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="giftcard-buy">
        <div className="font-display font-semibold text-[#071b22] mb-2">Buy a gift card</div>
        <div className="flex items-center gap-3 flex-wrap">
          <input type="number" min={10} step={5} value={amount} onChange={(e) => setAmount(Number(e.target.value))} className="rounded-lg border border-slate-200 px-3 py-2 text-sm w-32" data-testid="giftcard-amount" />
          <button onClick={buy} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium disabled:opacity-50" data-testid="giftcard-buy-btn">
            <Gift className="h-4 w-4" /> Buy via Stripe (test)
          </button>
        </div>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="giftcard-redeem">
        <div className="font-display font-semibold text-[#071b22] mb-2">Redeem a code</div>
        <div className="flex items-center gap-3 flex-wrap">
          <input value={redeemCode} onChange={(e) => setRedeemCode(e.target.value)} placeholder="GCW-XXXXXX" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="giftcard-code" />
          <button onClick={redeem} disabled={!redeemCode.trim()} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium" data-testid="giftcard-redeem-btn">
            Redeem
          </button>
        </div>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="giftcard-list">
        <div className="px-4 py-3 border-b border-slate-100 font-display font-semibold text-[#071b22]">Your gift cards</div>
        {cards.length === 0 ? <div className="p-6 text-sm text-[#252A34]/60 text-center">No gift cards.</div> : (
          <ul className="divide-y">
            {cards.map((c) => (
              <li key={c.id} className="px-4 py-3 text-sm flex items-center gap-3" data-testid={`giftcard-row-${c.id}`}>
                <div className="flex-1">
                  <div className="font-mono">{c.code}</div>
                  <div className="text-xs text-[#252A34]/60">Balance ${(c.balance || 0).toFixed(2)} / ${c.initial_amount.toFixed(2)} · {c.status}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ReferralsTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/referrals/me").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="text-[#252A34]/60">Loading…</div>;
  const code = data.referral?.code;
  const link = `${window.location.origin}/register?ref=${encodeURIComponent(code || "")}`;
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="referral-code">
        <div className="text-xs uppercase tracking-wider text-[#252A34]/60">Your code</div>
        <div className="mt-1 font-mono text-2xl font-semibold text-[#071b22]" data-testid="referral-code-value">{code}</div>
        <div className="text-xs text-[#252A34]/60 mt-2">Earn ${(data.referral?.reward_amount || 25).toFixed(2)} when a friend completes their first transaction.</div>
        <div className="mt-3 flex items-center gap-2">
          <input value={link} readOnly className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm bg-slate-50" data-testid="referral-link" />
          <button onClick={() => { navigator.clipboard.writeText(link); toast.success("Copied"); }} className="inline-flex items-center gap-1 rounded-full bg-[#165a71] text-white px-3 py-2 text-xs" data-testid="referral-copy">
            <Copy className="h-3 w-3" /> Copy
          </button>
        </div>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-5 grid grid-cols-2 gap-3" data-testid="referral-counts">
        <div>
          <div className="text-xs uppercase tracking-wider text-[#252A34]/60">Attributed</div>
          <div className="mt-1 font-display text-2xl font-semibold text-[#071b22]">{data.counts.attributed}</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-[#252A34]/60">Qualified</div>
          <div className="mt-1 font-display text-2xl font-semibold text-[#071b22]">{data.counts.qualified}</div>
        </div>
      </div>
    </div>
  );
}
